export class Masonry {
	init() {
		const masonryElements = document.querySelectorAll<HTMLElement>(".masonry");
		let timeout: number | null = null;
		
		masonryElements.forEach(masonryElement => {
			// Wait until the fonts have loaded as our font's are slightly larger than the default browser ones.
			if (document.fonts) {
				document.fonts.ready.then(() => {
					window.requestAnimationFrame(() => {
						this.computeMasonryHeights(masonryElement);
					});
				});
			} else {
				window.requestAnimationFrame(() => {
					this.computeMasonryHeights(masonryElement);
				});
			}

			const imagesInMasonry = Array.from(masonryElement.querySelectorAll("img"));

			imagesInMasonry
				.filter(x => !x.complete)
				.forEach(x => {
					x.addEventListener("load", () =>
						this.computeMasonryHeights(masonryElement)
					);
				});

			window.addEventListener("resize", () => {
				if (timeout !== null) {
					window.clearTimeout(timeout);
					timeout = null;
				}

				timeout = window.setTimeout(() => {
					this.computeMasonryHeights(masonryElement);
					timeout = null;
				}, 100);
			});
		});
	}

	private computeMasonryHeights(masonryElement: HTMLElement): void {
		if (
			masonryElement.firstElementChild?.classList.contains("epi-editContainer")
		) {
			return this.computeMasonryHeights(
				masonryElement.firstElementChild as HTMLElement
			);
		}
		// We will need to include the gap between rows in our calculations.
		const masonryStyles = getComputedStyle(masonryElement);
		const gap = parseInt(masonryStyles.rowGap);

		// If the masonry block is set to "nowrap" assume it's in a mobile mode and we don't need to compute the height for forcing it into multiple rows.
		if (masonryStyles.flexWrap === "nowrap") {
			masonryElement.style.height = "";
			return;
		}

		const masonryTiles = Array.from(masonryElement.children).map(x => ({
			element: x as HTMLElement,
			styles: getComputedStyle(x),
		}));
		// This is basically the equivalent of .GroupBy in C#
		const masonryTileGroups = masonryTiles.reduce(
			(
				prev: {
					[key: string]: { element: HTMLElement; styles: CSSStyleDeclaration }[]
				},
				curr,
				index
			) => {
				// If the masonry tiles don't have an order, assign one so they are into 2 groups.
				const order =
					curr.styles.order !== "0" ? curr.styles.order : `${(index % 2) + 1}`;
				if (curr.styles.order !== order) {
					curr.element.style.order = order;
				}

				prev[order] ??= [];
				prev[order].push(curr);
				return prev;
			},
			{}
		);

		// Sum up the height of the tiles in the column.
		const masonryColumnHeights = Object.keys(masonryTileGroups).map(key => {
			const masonryTilesGroup = masonryTileGroups[key];
			const tileHeights = masonryTilesGroup.map(masonryTile => {
				const height = parseFloat(masonryTile.styles.height) +
					parseFloat(masonryTile.styles.marginTop) +
					parseFloat(masonryTile.styles.marginBottom);

				masonryTile.element.setAttribute("data-height", `${height}px`);
				return height;
			});

			return (
				tileHeights.reduce(
					(sum, val) =>
						(sum += val)
					, 0) +
				// Add the additional height from the gap between the rows
				Math.max(masonryTilesGroup.length - 1, 0) * gap
			);
		});

		// Find the tallest column
		const masonryHeight = Math.max(...masonryColumnHeights);
		
		// Make the masonry element be the height of the tallest column so that they are divided up into columns.
		masonryElement.style.height = `${Math.ceil(masonryHeight)}px`;
	}
}

(() => {
	document.addEventListener("DOMContentLoaded", () => {
		const masonry = new Masonry();
		masonry.init();
	});
})();
